/*  ========================================================================
    # Sections - Card List
    ========================================================================  */
import { AnimatePresence, MotionConfig, motion, useInView } from 'motion/react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Card from '../../components/Card';
import ScrollSlider from '../../components/ScrollSlider';
import useBreakpoints from '../../hooks/useBreakpoints';
import useTranslation from '../../hooks/useTranslation';
import useGetParameter from '../../hooks/useGetParameter';

const CardList = ({
  displayType,
  displayedContent,
  cards,
  showFilter,
  filters,
  backgroundColor,
  openInNewTab,
  semanticHeadlineLevel
}) => {
  const __ = useTranslation();
  const wrapperRef = useRef(null);
  const filterWrapperRef = useRef(null);
  const isInView = useInView(wrapperRef, { once: true });
  const { xxlUp, xlUp, lgUp, mdUp, smUp } = useBreakpoints();
  const [filteredCards, setFilteredCards] = useState(cards);

  const [activeFilter, setActiveFilter] = useGetParameter('filter', 'string', 'all', [
    'all',
    ...(filters || []).map(filter => filter.slug)
  ]);

  const scrollByValue = useMemo(() => {
    if (xxlUp) {
      return 473;
    } else if (xlUp) {
      return 420;
    } else if (lgUp) {
      return 492;
    } else if (mdUp) {
      return 536;
    } else if (smUp) {
      return 536;
    } else {
      return 400;
    }
  }, [xxlUp, xlUp, lgUp, mdUp, smUp]);

  const motionListItemVariants = {
    initial: {
      opacity: 0,
      x: displayType === 'grid' ? 0 : 20,
      y: displayType === 'grid' ? 20 : 0
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0
    },
    exit: {
      opacity: 0,
      x: 0,
      y: 20
    }
  };

  const getButtonClass = filterSlug => {
    if (activeFilter === filterSlug && backgroundColor === 'green') {
      return 'c-button--orange';
    } else if (activeFilter === filterSlug) {
      return 'c-button--grey';
    } else if (backgroundColor === 'green') {
      return 'c-button--green';
    } else {
      return 'c-button--outline';
    }
  };

  const filterCards = filterSlug => {
    if (filterSlug === 'all' || null) {
      setFilteredCards(cards);
    } else {
      const newCards = [];
      cards.forEach(card => {
        if (card.filters && card.filters.length > 0) {
          let isInFilter = false;
          card.filters.forEach(filter => {
            if (filter.slug === filterSlug) {
              isInFilter = true;
            }
          });

          if (isInFilter) {
            newCards.push(card);
          }
        }
      });
      setFilteredCards(newCards);
    }
  };

  useEffect(() => {
    filterCards(activeFilter);
  }, [activeFilter]);

  useEffect(() => {
    if (filterWrapperRef.current) {
      // Handle keyboard navigation
      const buttons = filterWrapperRef.current.querySelectorAll('button');
      buttons.forEach((button, index) => {
        button.addEventListener('keydown', event => {
          if (event.key === 'ArrowRight') {
            if (index + 1 < buttons.length) {
              buttons[index + 1].focus();
            } else {
              buttons[0].focus();
            }
          } else if (event.key === 'ArrowLeft') {
            if (index - 1 >= 0) {
              buttons[index - 1].focus();
            } else {
              buttons[buttons.length - 1].focus();
            }
          }
        });
      });
    }
  }, [filterWrapperRef]);

  return (
    <MotionConfig reducedMotion='user'>
      {showFilter && filters && filters.length > 0 && (
        <div
          className='s-card-list__filter-wrapper container'
          role='toolbar'
          aria-label='Filter'
          aria-controls='cardlist'
          ref={filterWrapperRef}
        >
          <div className='s-card-list__filter'>
            <button
              type='button'
              aria-label={__('filter-show-all')}
              onClick={() => setActiveFilter('all')}
              className={`
                c-button ${getButtonClass('all')} c-button--small`}
              tabIndex='0'
              role='radio'
              aria-checked={activeFilter === 'all'}
            >
              {__('filter-show-all')}
            </button>
            {filters.map((filter, index) => (
              <button
                key={`filter-${index}`}
                type='button'
                aria-label={filter.name}
                onClick={() => setActiveFilter(filter.slug)}
                className={`
                  c-button ${getButtonClass(filter.slug)} c-button--small`}
                tabIndex='-1'
                role='radio'
                aria-checked={activeFilter === filter.slug}
              >
                {filter.name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div
        className={`s-card-list__${displayType}-wrapper ${displayType === 'grid' ? 'container' : ''}`}
        ref={wrapperRef}
      >
        <div className={`s-card-list__${displayType}`} id='cardlist' role='grid'>
          {displayType === 'slider' && <div className='s-card-list__spacer'></div>}
          <AnimatePresence mode='popLayout'>
            {filteredCards.map((card, index) => (
              <motion.div
                variants={motionListItemVariants}
                initial='initial'
                animate={isInView ? 'visible' : 'initial'}
                exit='exit'
                transition={{ delay: 0.1 * index }}
                className='s-card-list__item'
                key={`list-item-${card.id}-${index}`}
                layout='position'
              >
                <Card
                  headline={card.headline}
                  subheadline={card.subheadline}
                  description={card.description}
                  hasLightboxContent={card.hasLightboxContent}
                  lightboxDescription={card.lightboxDescription}
                  image={card.image}
                  url={card.url}
                  hideButton={card.hide_button}
                  fromPrice={card.fromPrice}
                  fixPrice={card.fixPrice}
                  type={displayedContent}
                  label={card.label}
                  openInNewTab={openInNewTab}
                  headlineLevel={semanticHeadlineLevel}
                />
              </motion.div>
            ))}
            {filteredCards.length === 0 && (
              <motion.div
                variants={motionListItemVariants}
                initial='initial'
                animate={isInView ? 'visible' : 'initial'}
                exit='exit'
                transition={{ delay: 0.1 }}
                className='s-card-list__item s-card-list__item--no-card'
                layout='position'
              >
                <div className='s-card-list__no-card wysiwyg-block'>
                  <p className='h5'>{__('card-list-not-found')}</p>
                  <p dangerouslySetInnerHTML={{ __html: __('card-list-not-found-subline') }} />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          {displayType === 'slider' && <div className='s-card-list__spacer'></div>}
        </div>
      </div>
      {displayType === 'slider' && (
        <div className='container'>
          <ScrollSlider scrollBy={scrollByValue} scrollAreaRef={wrapperRef} />
        </div>
      )}
    </MotionConfig>
  );
};

CardList.propTypes = {
  displayType: PropTypes.string,
  displayedContent: PropTypes.string,
  cards: PropTypes.array,
  showFilter: PropTypes.bool,
  filters: PropTypes.oneOfType([PropTypes.array, PropTypes.bool]),
  backgroundColor: PropTypes.string,
  openInNewTab: PropTypes.bool,
  semanticHeadlineLevel: PropTypes.string
};

export default CardList;
